import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AppConfig } from '../config/app-config';
import { AppConfigStore } from '../app-config/app-config.store';
import { CmsBackgroundComponent } from './cms-background.component';
import { isMaintenanceEnabled } from '../utils/maintenance-gate';

/**
 * Standalone full-page Maintenance screen when CMS
 * `maintenance_mode.isMaintenanceEnabled` is true.
 *
 * Layout matches product / install-required: CMS login background, centered
 * brand mark + title/subtitle, optional CTA pill. Independent of route/auth.
 * CTA force-refetches App Config and reloads only when maintenance is off;
 * fetch failure keeps the user here. Title/subtitle prefer CMS `eventPayload`,
 * then i18n defaults. CTA label comes from CMS `showRetry` (string) via
 * `ctaLabel` input — `undefined` hides the button.
 */
@Component({
  selector: 'app-maintenance',
  standalone: true,
  imports: [TranslatePipe, CmsBackgroundComponent],
  template: `
    <app-cms-background
      fallback="login"
      containerClass="flex items-center justify-center px-6 py-12"
    >
      <div
        class="flex w-full max-w-5xl flex-col items-center gap-12 text-center"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="maintenance-title"
        aria-describedby="maintenance-desc"
      >
        <!-- Figma: 24px between logo / title / subtitle; 48px from that stack to the CTA. -->
        <div class="flex flex-col items-center gap-6">
          <img
            src="/icons/ic-maintenance.svg"
            alt=""
            width="64"
            height="64"
            class="h-16 w-16 select-none"
            decoding="async"
            aria-hidden="true"
          />

          <!-- Figma: title 900 / 40 / 40 / -3%; subtitle 500 / 24 / 32 / -0.5% at white 78%. -->
          <h1
            id="maintenance-title"
            class="whitespace-nowrap text-center text-[40px] font-black leading-10 tracking-[-0.03em] text-white"
          >
            @if (cmsTitle()) {
              {{ cmsTitle() }}
            } @else {
              {{ 'common.maintenance.title' | translate }}
            }
          </h1>

          <p
            id="maintenance-desc"
            class="max-w-xl text-center text-2xl font-medium leading-8 tracking-[-0.005em] text-white/[0.78]"
          >
            @if (cmsSubtitle()) {
              {{ cmsSubtitle() }}
            } @else {
              {{ 'common.maintenance.body' | translate: { appName } }}
            }
          </p>
        </div>

        @if (ctaLabel() !== undefined) {
          <div class="flex w-full flex-col items-center">
            <button
              type="button"
              class="inline-flex h-16 min-w-80 max-w-full cursor-pointer items-center justify-center rounded-full border-0 bg-[#853DD6] px-10 text-2xl font-bold leading-8 tracking-[-0.005em] text-white shadow-lg shadow-purple-900/20 transition-all duration-200 hover:bg-[#7534BD] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-70"
              [disabled]="retrying()"
              (click)="onGoHome()"
            >
              @if (retrying()) {
                {{ 'common.maintenance.retrying' | translate }}
              } @else if (ctaLabel()) {
                {{ ctaLabel() }}
              } @else {
                {{ 'common.maintenance.retry' | translate }}
              }
            </button>
          </div>
        }
      </div>
    </app-cms-background>
  `,
})
export class MaintenanceComponent {
  private readonly appConfigStore = inject(AppConfigStore);

  /**
   * CMS-resolved CTA label. `undefined` hides the button; empty string uses
   * i18n fallback; non-empty string is shown as-is.
   */
  readonly ctaLabel = input<string | undefined>(undefined);

  readonly appName = AppConfig.JIO_CLOUD_PC_APP_NAME;
  readonly retrying = signal(false);

  private readonly eventPayload = computed(
    () => this.appConfigStore.configs()?.maintenance_mode?.eventPayload,
  );
  readonly cmsTitle = computed(() => {
    const title = this.eventPayload()?.title?.trim();
    return title ? title : null;
  });
  readonly cmsSubtitle = computed(() => {
    const subtitle = this.eventPayload()?.subtitle?.trim();
    return subtitle ? subtitle : null;
  });

  async onGoHome(): Promise<void> {
    if (this.retrying()) return;
    this.retrying.set(true);
    try {
      await this.appConfigStore.ensureLoaded(true);
      if (!isMaintenanceEnabled(this.appConfigStore.configs()?.maintenance_mode)) {
        window.location.reload();
        return;
      }
    } catch {
      // Fetch failure/timeout while already in maintenance — stay put (cannot confirm exit).
    } finally {
      this.retrying.set(false);
    }
  }
}
